import { faker } from '@faker-js/faker';
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Material UI
import { useTheme } from '@mui/material/styles';
import { Grid, Container, Typography, Card, Box, IconButton, Button, Stack, Tooltip, CircularProgress, Paper } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SyncIcon from '@mui/icons-material/Sync';
import axios from 'axios';

// Components
import Page from '../components/Page';

// Sections
import {
  AppWidgetSummary,
  AppWebsiteVisits,
  AppCurrentVisits,
  AppConversionRates,
  AppNewsUpdate,
  AppCurrentSubject
} from '../sections/@dashboard/app';

import { authState } from '../../../Redux/authSlice';
import { useLocation } from 'react-router-dom';

const TaskBoard = ({ events, navigate }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tasks = events.filter(e => e.type === 'task');
  const reminders = events.filter(e => e.type === 'reminder');

  const overdueTasks = tasks.filter(t => {
    if (!t.date) return false;
    const d = new Date(t.date);
    d.setHours(0, 0, 0, 0);
    return d < today;
  });

  const pendingTasks = tasks.filter(t => {
    if (!t.date) return true;
    const d = new Date(t.date);
    d.setHours(0, 0, 0, 0);
    return d >= today;
  });

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">My Dashboard</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Filtered by your assignments</Typography>
      </Box>
      <Grid container spacing={3} sx={{ p: 3, flexGrow: 1, overflow: 'auto', maxHeight: 450, bgcolor: '#f4f6f8' }}>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="info.main" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 10, height: 10, bgcolor: 'info.main', borderRadius: '50%', mr: 1 }} />
            UPCOMING REMINDERS ({reminders.length})
          </Typography>
          <Stack spacing={2}>
            {reminders.length === 0 ? <Typography variant="body2" color="text.secondary">No upcoming reminders.</Typography> : reminders.map(reminder => (
              <Paper key={reminder.id} variant="outlined" sx={{ p: 2, borderLeft: '4px solid', borderLeftColor: 'info.main', cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4, bgcolor: '#e3f2fd' } }} onClick={() => reminder.matterId && navigate(`/matter/${reminder.matterId}`)}>
                <Typography variant="subtitle2">{reminder.title}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>{reminder.matterName}</Typography>
                <Typography variant="caption" color="text.primary" sx={{ mt: 1, display: 'block' }}>Due: {reminder.date}</Typography>
              </Paper>
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="error.main" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 10, height: 10, bgcolor: 'error.main', borderRadius: '50%', mr: 1 }} />
            MY OVERDUE TASKS ({overdueTasks.length})
          </Typography>
          <Stack spacing={2}>
            {overdueTasks.length === 0 ? <Typography variant="body2" color="text.secondary">No overdue tasks.</Typography> : overdueTasks.map(task => (
              <Paper key={task.id} variant="outlined" sx={{ p: 2, borderLeft: '4px solid', borderLeftColor: 'error.main', cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4, bgcolor: '#fff0f0' } }} onClick={() => task.matterId && navigate(`/matter/${task.matterId}`)}>
                <Typography variant="subtitle2">{task.title.replace('Task: ', '')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>{task.matterName}</Typography>
                <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold', mt: 1, display: 'block' }}>Due: {task.date}</Typography>
              </Paper>
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="warning.main" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 10, height: 10, bgcolor: 'warning.main', borderRadius: '50%', mr: 1 }} />
            MY PENDING TASKS ({pendingTasks.length})
          </Typography>
          <Stack spacing={2}>
            {pendingTasks.length === 0 ? <Typography variant="body2" color="text.secondary">No pending tasks.</Typography> : pendingTasks.map(task => (
              <Paper key={task.id} variant="outlined" sx={{ p: 2, borderLeft: '4px solid', borderLeftColor: 'warning.main', cursor: 'pointer', transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4, bgcolor: '#fffdf5' } }} onClick={() => task.matterId && navigate(`/matter/${task.matterId}`)}>
                <Typography variant="subtitle2">{task.title.replace('Task: ', '')}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>{task.matterName}</Typography>
                <Typography variant="caption" color="text.primary" sx={{ mt: 1, display: 'block' }}>Due: {task.date}</Typography>
              </Paper>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
};

const DashboardCalendar = ({ events, onSync, syncing, isConnected, navigate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = new Date(year, month, 1).getDay();

  const days = Array(firstDayIndex).fill(null).concat(Array.from({length: daysInMonth}, (_, i) => i + 1));

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Calendar (Tasks, Meetings & Outlook)</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
            {isConnected ? (
                <Button size="small" variant="outlined" startIcon={<SyncIcon />} onClick={onSync} disabled={syncing}>
                    {syncing ? 'Syncing...' : 'Sync Outlook'}
                </Button>
            ) : (
                <Button size="small" variant="outlined" color="warning" href="/email">
                    Connect Outlook
                </Button>
            )}
            <IconButton onClick={() => setCurrentDate(new Date(year, month - 1, 1))}><ChevronLeftIcon/></IconButton>
            <Typography fontWeight="bold" sx={{ minWidth: 140, textAlign: 'center' }}>
                {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </Typography>
            <IconButton onClick={() => setCurrentDate(new Date(year, month + 1, 1))}><ChevronRightIcon/></IconButton>
        </Stack>
      </Stack>
      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
            <Typography key={d} align="center" fontWeight="bold" color="text.secondary" sx={{ py: 1 }}>{d}</Typography>
        ))}
        {days.map((day, idx) => {
            const dayEvents = day ? events.filter(e => {
                if (!e.date) return false;
                const dateParts = e.date.split('T')[0].split('-');
                if (dateParts.length !== 3) return false;
                const [y, m, d] = dateParts;
                return parseInt(d, 10) === day && parseInt(m, 10) - 1 === month && parseInt(y, 10) === year;
            }) : [];
            const isToday = day === new Date().getDate() && month === new Date().getMonth() && year === new Date().getFullYear();

            return (
                <Box key={idx} sx={{ minHeight: 110, border: '1px solid', borderColor: 'divider', p: 1, bgcolor: day ? (isToday ? '#f0f7ff' : 'background.paper') : '#f9fafb', borderRadius: 1 }}>
                    {day && <Typography variant="caption" fontWeight={isToday ? 'bold' : 'normal'} color={isToday ? 'primary.main' : 'text.primary'}>{day}</Typography>}
                    <Stack spacing={0.5} mt={0.5}>
                        {dayEvents.map(ev => (
                            <Tooltip title={ev.title} key={ev.id} placement="top" arrow>
                                <Box onClick={() => { if (ev.matterId && navigate) navigate(`/matter/${ev.matterId}`) }} sx={{ fontSize: '0.65rem', p: 0.5, borderRadius: 0.5, bgcolor: ev.type === 'outlook' ? '#e3f2fd' : (ev.type === 'meeting' ? '#e8f5e9' : (ev.type === 'reminder' ? '#ffecb3' : '#fff3e0')), color: ev.type === 'outlook' ? '#0d47a1' : (ev.type === 'meeting' ? '#1b5e20' : (ev.type === 'reminder' ? '#ff6f00' : '#e65100')), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>
                                    {ev.title}
                                </Box>
                            </Tooltip>
                        ))}
                    </Stack>
                </Box>
            );
        })}
      </Box>
      <Stack direction="row" spacing={3} mt={3} justifyContent="center">
          <Stack direction="row" alignItems="center" spacing={0.5}><Box sx={{ width: 12, height: 12, bgcolor: '#e8f5e9', borderRadius: '50%' }}/><Typography variant="caption">System Meetings</Typography></Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}><Box sx={{ width: 12, height: 12, bgcolor: '#fff3e0', borderRadius: '50%' }}/><Typography variant="caption">System Tasks</Typography></Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}><Box sx={{ width: 12, height: 12, bgcolor: '#ffecb3', borderRadius: '50%' }}/><Typography variant="caption">Reminders</Typography></Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}><Box sx={{ width: 12, height: 12, bgcolor: '#e3f2fd', borderRadius: '50%' }}/><Typography variant="caption">Outlook Events</Typography></Stack>
      </Stack>
    </Card>
  );
};

export const CoreApp = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const auth = useSelector(authState);
  console.log("Core State: ", { auth });

  const [caseStats, setCaseStats] = useState({
    inprogress: 0,
    open: 0,
    pending: 0,
    closed: 0,
  });

  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [outlookEvents, setOutlookEvents] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [user, setUser] = useState(null);
  const [assignedMatters, setAssignedMatters] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(true);
  
  const syncOutlook = useCallback(async (token) => {
    if (!token) return;
    setSyncing(true);
    try {
        const res = await axios.get('https://graph.microsoft.com/v1.0/me/events?$select=subject,start,end&$top=100', {
            headers: { Authorization: `Bearer ${token}` }
        });
        const outEvents = res.data.value.map(evt => ({
            id: `outlook-${evt.id}`,
            title: `Outlook: ${evt.subject}`,
            date: evt.start?.dateTime?.split('T')[0],
            type: 'outlook'
        }));
        setOutlookEvents(outEvents);
    } catch (e) {
        console.error("Outlook sync failed", e);
    } finally {
        setSyncing(false);
    }
  }, []); // Empty dependency array: this function doesn't depend on any props or state.


  useEffect(() => {
    if (auth === undefined || auth === null) return; // Wait until auth is ready
  
    const fetchAssignedFiles = async () => {
      try {
        const response = await axios.get('https://demo.insollex.com/api/legal-matter/assigned', { 
            withCredentials: true 
        });
        setAssignedMatters(response.data.matters || []);
      } catch (error) {
        console.error('Failed to load assigned matters:', error);
      } finally {
        setLoadingAssigned(false);
      }
    };
  
    const fetchBlogs = async () => {
      try {
        const response = await fetch('https://demo.insollex.com/api/post');
        const data = await response.json();
        setBlogs(data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoadingBlogs(false);
      }
    };
  
    const fetchNewDbStats = async () => {
      try {
        const response = await fetch(`https://demo.insollex.com/api/legal-matter/status?t=${Date.now()}`);
        if (!response.ok) throw new Error(`New DB failed with status ${response.status}`);
        return await response.json();
      } catch (error) {
        console.error('❌ Error fetching NEW DB case stats:', error);
        return {};
      }
    };
    
    const fetchCaseStats = async () => {
      setLoading(true);
      try {
        const newDbData = await fetchNewDbStats();
    
        const combinedStats = {
          inprogress: newDbData.inprogress || 0,
          open: newDbData.open || 0,
          pending: newDbData.pending || 0,
          closed: newDbData.closed || 0,
        };
    
        setCaseStats(combinedStats);
        console.log("Fetched Case Stats:", combinedStats);
      } catch (error) {
        console.error("Error fetching case stats:", error);
      } finally {
        setLoading(false);
      }
    };
  
    const fetchCalendarData = async (userId) => {
      try {
          const res = await axios.get('https://demo.insollex.com/api/dashboard/calendar', { withCredentials: true });
          setCalendarEvents(res.data.events || []);
          
          const token = localStorage.getItem(`outlook_token_${userId}`);
          if (token) await syncOutlook(token);
      } catch (e) {
          console.error("Failed to fetch calendar data", e);
      }
    };

    if (!auth) {
      console.log('Not authenticated, redirecting...');
      navigate('/', { replace: true });
      return;
    }
  
    console.log('Auth verified, fetching stats...');
    fetchCaseStats();
    fetchBlogs();
    
    axios.get('https://demo.insollex.com/api/user', { withCredentials: true })
         .then(res => {
             setUser(res.data);
             fetchCalendarData(res.data.id);
             fetchAssignedFiles();
         })
         .catch(err => console.error(err));
  }, [auth, navigate, location.pathname, syncOutlook]);

  if (loading || loadingBlogs) {
    return (
      <Page title="Legal Dashboard">
        <Container maxWidth={false}>
          <Typography variant="h6" align="center">Loading data...</Typography>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Legal Dashboard">
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          {/* In Progress Cases Widget */}
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary
              title="In Progress Cases"
              total={caseStats.inprogress}
              icon={'fa6-solid:hourglass-half'}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * In Progress Estate cases across departments. Data is regularly updated.
            </Typography>
          </Grid>

          {/*Completed Cases Widget*/}
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary
              title="Open Cases"
              total={caseStats.open}
              color="info"
              icon={'a6-solid:gavel'}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Total number of Estate cases resolved successfully.
            </Typography>
          </Grid>

          {/* Pending Cases Widget */}
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary
              title="Pending Cases"
              total={caseStats.pending}
              color="warning"
              icon={'fa6-solid:hourglass-half'}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Pending cases awaiting further actions or court dates.
            </Typography>
          </Grid>

          {/* Closed Cases Widget */}
          <Grid item xs={12} sm={6} md={3}>
            <AppWidgetSummary
              title="Closed Cases"
              total={caseStats.closed}
              color="error"
              icon={'fa6-solid:gavel'}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Estate rulings received across various jurisdictions.
            </Typography>
          </Grid>

          {/* The rest of the sections with fake data */}

          <Grid item xs={12} md={6} lg={8}>
            <AppWebsiteVisits
              title="Case Success Rate"
              subheader="(+13%) improvement this year"
              chartLabels={['Q1', 'Q2', 'Q3', 'Q4']}
              chartData={[ 
                { name: 'Criminal Cases', type: 'column', fill: 'solid', data: [35, 42, 51, 60] },
                { name: 'Civil Cases', type: 'area', fill: 'gradient', data: [65, 58, 52, 50] },
                { name: 'Family Cases', type: 'line', fill: 'solid', data: [50, 48, 55, 60] },
              ]}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Success rate is based on resolved cases in different legal areas.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AppCurrentVisits
              title="Cases by Type"
              chartData={[
                { label: 'Criminal', value: 120 },
                { label: 'Civil', value: 150 },
                { label: 'Family', value: 60 },
                { label: 'Corporate', value: 90 },
              ]}
              chartColors={[
                theme.palette.primary.main,
                theme.palette.chart.blue[0],
                theme.palette.chart.violet[0],
                theme.palette.chart.yellow[0],
              ]}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Breakdown of active cases by their respective legal categories.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={8}>
            <AppConversionRates
              title="Legal Team Performance"
              subheader="(+8%) growth compared to last year"
              chartData={[
                { label: 'Team A', value: 400 },
                { label: 'Team B', value: 430 },
                { label: 'Team C', value: 448 },
                { label: 'Team D', value: 470 },
                { label: 'Team E', value: 540 },
              ]}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Legal team performance based on case completion and client satisfaction.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={4}>
            <AppCurrentSubject
              title="Legal Areas Focused On"
              chartLabels={['Criminal Law', 'Civil Law', 'Family Law', 'Corporate Law']}
              chartData={[
                { name: 'Team A', data: [80, 90, 60, 70] },
                { name: 'Team B', data: [70, 60, 50, 85] },
                { name: 'Team C', data: [55, 65, 50, 60] },
              ]}
              chartColors={[...Array(4)].map(() => theme.palette.text.secondary)}
            />
            <Typography variant="body2" color="textSecondary" align="center">
              * Legal teams are specialized in different law sectors. Performance varies by area.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6} lg={8}>
  <AppNewsUpdate
    title="Legal News Update"
    list={
      blogs && Array.isArray(blogs)
        ? blogs
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) // Sort by latest
            .slice(0, 4) // Take the latest 4 blogs
            .map((blog) => ({
              id: blog.id,
              title: blog.title,
              description: blog.content
                ? blog.content.split('\n')[0]
                : "No description available.",
              image: blog.imageUrl ? blog.imageUrl : faker.image.abstract(),
              postedAt: new Date(blog.createdAt),
            }))
        : []
    }
    onViewAllClick={() => navigate('/blog')} // Navigate to the /blogs page when View All is clicked
  />
  <Typography variant="body2" color="textSecondary" align="center">
    * Latest updates from the legal field. Stay informed with changes in law.
  </Typography>
</Grid>

          {/* Administrator File Allocation Widget */}
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 3, pb: 2 }}>
                <Typography variant="h6">Files Allocated To You</Typography>
              </Box>
              <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto', maxHeight: 400 }}>
                {loadingAssigned ? (
                  <Box display="flex" justifyContent="center"><CircularProgress /></Box>
                ) : assignedMatters.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center">You have no assigned files.</Typography>
                ) : (
                  <Stack spacing={2}>
                    {assignedMatters.map((matter) => (
                      <Paper key={matter.id} variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ overflow: 'hidden', mr: 2 }}>
                          <Typography variant="subtitle2" noWrap>{matter.matterName || 'Unnamed Matter'}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">Ref: {matter.estateNo || 'N/A'}</Typography>
                        </Box>
                        <Button size="small" variant="contained" onClick={() => navigate(`/matter/${matter.id}`)} sx={{ flexShrink: 0 }}>Open</Button>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Task Board Widget */}
          <Grid item xs={12}>
            <TaskBoard events={calendarEvents} navigate={navigate} />
            <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
              * Your personalized Task Board displaying tasks assigned specifically to you. Click a task to open the matter.
            </Typography>
          </Grid>

          {/* Full-width Calendar Integration Widget */}
          <Grid item xs={12}>
            <DashboardCalendar
              events={[...calendarEvents, ...outlookEvents]}
              onSync={() => syncOutlook(localStorage.getItem(`outlook_token_${user?.id}`))}
              isConnected={!!localStorage.getItem(`outlook_token_${user?.id}`)}
              syncing={syncing}
              navigate={navigate}
            />
            <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
              * Interactive calendar view mapping out internal tasks, case meetings, and synchronized Microsoft Outlook events.
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};
